import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ItiLink as Link, Loader } from 'components';
import { SelectedLocationContext } from 'contexts';
import dayjs from 'dayjs';
import { default as timezone } from 'dayjs/plugin/timezone';
import { default as utc } from 'dayjs/plugin/utc';
import { useContext, useEffect, useState } from 'react';
import { generateUnitInvoices, getUnitCredits } from 'services';
import { getContractForSizeType } from 'services/contracts';
import { ActiveUnit, BillableEvent, Contract, InvoiceItem, Transaction } from 'types';

dayjs.extend(utc);
dayjs.extend(timezone);

const DollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});
const gridTemplateColumns = '1fr 1fr 1fr .5fr';

const UnitInvoice = (props: {
  unit: ActiveUnit;
  gateInTransaction?: Transaction;
  gateOutTransaction?: Transaction;
  refreshInvoices?: number;
}) => {
  const [startDate, setStartDate] = useState(
    !!props.gateInTransaction?.completedDate
      ? dayjs.utc(props.gateInTransaction.completedDate).startOf('day')
      : dayjs.utc().startOf('month')
  );
  const [endDate, setEndDate] = useState(
    (!!props.gateOutTransaction ? dayjs.utc(props.gateOutTransaction.completedDate) : dayjs.utc()).endOf('day')
  );
  const [loading, setLoading] = useState(false);
  const [contract, setContract] = useState<Contract | null>(null);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>();
  const [unit, setUnit] = useState(props.unit);
  const [unitCredits, setUnitCredits] = useState<BillableEvent[]>();
  const { selectedLocation } = useContext(SelectedLocationContext);

  const unitId = unit.id;
  const customerId = unit.customer?.id;

  useEffect(() => {
    setUnit(props.unit);
  }, [props.unit]);

  useEffect(() => {
    const next = !!props.gateInTransaction?.completedDate
      ? dayjs.utc(props.gateInTransaction.completedDate).startOf('day')
      : dayjs.utc().startOf('month');
    // Preserve the existing reference when the instant is unchanged so the contract effect below
    // (which depends on startDate) doesn't re-fetch on every render.
    setStartDate(prev => (prev.valueOf() === next.valueOf() ? prev : next));
  }, [props.gateInTransaction?.completedDate]);

  useEffect(() => {
    const next = !!props.gateOutTransaction?.completedDate
      ? dayjs.utc(props.gateOutTransaction.completedDate).endOf('day')
      : dayjs.utc().endOf('day');
    setEndDate(prev => (prev.valueOf() === next.valueOf() ? prev : next));
  }, [props.gateOutTransaction?.completedDate]);

  /* The contract lookup only varies by size type, customer, start date and location — endDate is
     not part of the request, so it is kept out of this effect to avoid redundant 404s. */
  useEffect(() => {
    if (isNaN(unitId) || !unit.sizeType || !customerId || !selectedLocation) {
      setContract(null);
      return;
    }
    getContractForSizeType({
      sizeType: unit.sizeType,
      customerId: customerId,
      date: startDate.tz(selectedLocation.timeZone, true).toDate(),
      locationId: selectedLocation.id
    }).then(setContract);
  }, [unit.sizeType, unitId, customerId, startDate, selectedLocation]);

  /* Invoices and credits depend on the full date range and the refresh trigger. */
  useEffect(() => {
    if (isNaN(unitId) || !unit.sizeType || !customerId || !selectedLocation) {
      return;
    }
    setLoading(true);
    generateUnitInvoices({
      unitId,
      locationId: selectedLocation.id,
      from: startDate.toDate(),
      to: endDate.toDate()
    }).then(res => {
      setInvoiceItems(res);
      setLoading(false);
    });
    getUnitCredits(unitId).then(res => setUnitCredits(res));
  }, [unit.sizeType, unitId, customerId, startDate, endDate, selectedLocation, props.refreshInvoices]);

  return (
    <Loader loading={loading}>
      <div className="card-primary">
        <div className="card-header px-3">
          <h4>UNIT INVOICE</h4>
        </div>
        <div className="card-body">
          {/* Contract and Date Selection Group */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            {contract ? (
              <div className="d-flex flex-column">
                <h6 className="text-dark font-weight-regular">Contract</h6>
                <Link to={`/contracts/${contract.id}`}>{contract.id}</Link>
              </div>
            ) : (
              <div>
                <label>Contract</label>
                <p>No contract available</p>
              </div>
            )}
            <div className="d-flex flex-row g-3">
              <div>
                <label>Start Date</label>
                <div className="mt-2">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      sx={{ backgroundColor: 'var(--white)' }}
                      slotProps={{
                        textField: { margin: 'none', required: true, size: 'small', className: 'w-100' }
                      }}
                      value={startDate}
                      onChange={date => setStartDate(date!)}
                    />
                  </LocalizationProvider>
                </div>
              </div>
              <div>
                <label>End Date</label>
                <div className="mt-2">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      sx={{ backgroundColor: 'var(--white)' }}
                      slotProps={{
                        textField: { margin: 'none', required: true, size: 'small', className: 'w-100' }
                      }}
                      minDate={startDate}
                      value={endDate}
                      onChange={date => setEndDate(date!)}
                    />
                  </LocalizationProvider>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Total Cost */}
          {!!contract && (
            <div>
              <div>
                <ul className="list-group">
                  {/* Header Row */}
                  <li className="list-group-item list-group-header grid-12" style={{ gridTemplateColumns }}>
                    <p>Type</p>
                    <p>Quantity</p>
                    <p></p>
                    <p>Total</p>
                  </li>
                  {invoiceItems && (
                    <>
                      {invoiceItems
                        .filter(i => i.chargedDays > 0)
                        .map((item, i) => {
                          return (
                            <li key={i} className="list-group-item grid-10 text-base" style={{ gridTemplateColumns }}>
                              <p>Days In</p>
                              <p>{item.chargedDays}</p>
                              <p></p>
                              <p>{DollarFormatter.format(item.storageCharge)}</p>
                            </li>
                          );
                        })}
                      <li className="list-group-item grid-10 text-base" style={{ gridTemplateColumns }}>
                        <p>Gate In</p>
                        <p></p>
                        <p></p>
                        <p>{DollarFormatter.format(invoiceItems.reduce((sum, i) => (sum += i.gateInCharge), 0))}</p>
                      </li>
                      <li className="list-group-item grid-10 text-base" style={{ gridTemplateColumns }}>
                        <p>Gate Out</p>
                        <p></p>
                        <p></p>
                        <p>{DollarFormatter.format(invoiceItems.reduce((sum, i) => (sum += i.gateOutCharge), 0))}</p>
                      </li>
                      <li className="list-group-item grid-10 text-base" style={{ gridTemplateColumns }}>
                        <p>Lift Off</p>
                        <p>{invoiceItems.reduce((sum, i) => (sum += i.liftOffCount), 0)}</p>
                        <p></p>
                        <p>{DollarFormatter.format(invoiceItems.reduce((sum, i) => (sum += i.liftOffCharge), 0))}</p>
                      </li>
                      <li className="list-group-item grid-10 text-base" style={{ gridTemplateColumns }}>
                        <p>Lift On</p>
                        <p>{invoiceItems.reduce((sum, i) => (sum += i.liftOnCount), 0)}</p>
                        <p></p>
                        <p>{DollarFormatter.format(invoiceItems.reduce((sum, i) => (sum += i.liftOnCharge), 0))}</p>
                      </li>
                      <li className="list-group-item grid-10 text-base" style={{ gridTemplateColumns }}>
                        <p>Dig</p>
                        <p>{invoiceItems.reduce((sum, i) => (sum += i.digCount), 0)}</p>
                        <p></p>
                        <p>{DollarFormatter.format(invoiceItems.reduce((sum, i) => (sum += i.digCharge), 0))}</p>
                      </li>
                      <li className="list-group-item grid-10 text-base" style={{ gridTemplateColumns }}>
                        <p>Other Events</p>
                        <p>{invoiceItems.reduce((sum, i) => (sum += i.otherEventCount), 0)}</p>
                        <p></p>
                        <p>{DollarFormatter.format(invoiceItems.reduce((sum, i) => (sum += i.otherEventCharge), 0))}</p>
                      </li>
                      <li className="list-group-item grid-10 text-base" style={{ gridTemplateColumns }}>
                        <p>Credits</p>
                        <p>{unitCredits?.reduce((sum, u) => (sum += !!u.cost && u.cost > 0 ? 1 : 0), 0) || 0}</p>
                        <p></p>
                        <p>{DollarFormatter.format(unitCredits?.reduce((sum, u) => (sum += u.cost), 0) || 0)}</p>
                      </li>
                      <div className="grid-1 mt-4 justify-items-end">
                        <div className="d-flex g-3">
                          <p>Sub Total</p>
                          <p>
                            {DollarFormatter.format(invoiceItems.reduce((sum, i) => (sum += i.getAllCharges()), 0))}
                          </p>
                        </div>
                        <div className="d-flex g-2">
                          <p>Unit Credit</p>
                          <p>- {DollarFormatter.format(unitCredits?.reduce((sum, u) => (sum += u.cost), 0) || 0)}</p>
                        </div>
                        <div className="d-flex g-3">
                          <p className="font-weight-bold">Grand Total</p>
                          <p className="font-weight-bold">
                            {DollarFormatter.format(
                              invoiceItems.reduce((sum, i) => (sum += i.getAllCharges()), 0) -
                                (unitCredits?.reduce((sum, u) => (sum += u.cost), 0) || 0)
                            )}
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </Loader>
  );
};

export default UnitInvoice;
